import {Button, Col, Row} from "reactstrap";
import {CustomInput} from "../../Common/Components/Input/CustomInput";
import RequestReimbursementMileage from "../Models/Request/RequestReimbursementMileage";
import {CustomButton} from "../../Common/Components/Button/CustomButton";
import RequestPassenger from "../Models/Request/RequestPassenger";
import React, {useEffect, useRef, useState} from "react";
import {CustomSubmitButton} from "../../Common/Components/Input/CustomSubmitButton";
import _ from "lodash";
import {useTranslation} from "react-i18next";
import {ToCustomDecimal} from "../../AppSystem/Helpers/ToCustomDecimal";
import { SlidePanel } from "../../Common/Components/Modal/SlidePanel";
import { CheckMobile } from '../../AppSystem/Helpers/CheckMobile';

export function ReimbursementMileage({ data, onChange, onValidate, onCancel, editMode = false }) {
    const { t } = useTranslation();
    const isMobile = CheckMobile(998);

    const [cancelModel, setCancelModel] = useState(data);
    const [panelOpen, setPanelOpen] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    const formDivRefMileage = useRef(null);
    const hiddenInputRef = useRef(null);
    const initialDataRef = useRef(null);

    const captureSnapshot = () => {
        initialDataRef.current = _.cloneDeep(data);
    };

    useEffect(() => {
        if (editMode === true) {
            const copyModel = _.cloneDeep(data);
            setCancelModel(copyModel);
            captureSnapshot();
            setPanelOpen(true);
        }
    }, []);

    const handleClose = () => {
        setPanelOpen(false);
        if (editMode) {
            onCancel(cancelModel);
        }
    };

    useEffect(() => {
        if (panelOpen) {
            if (isMobile) {
                setTimeout(() => {
                    const input = formDivRefMileage.current?.querySelector('input:not([type="hidden"])');
                    if (input) {
                        input.focus();
                        // Forzar apertura del teclado en algunos Android
                        input.click();
                    }
                }, 350);
            } else {
                // En desktop funciona normal
                setTimeout(() => {
                    const input = formDivRefMileage.current?.querySelector('input');
                    input?.focus();
                }, 300);
            }
        }
    }, [panelOpen]);

    const isButtonDisabled = !data?.from || data.from.trim() === '' ||
                         !data?.to   || data.to.trim()   === '' ||
                         !data?.km   || data.km <= 0;
    const hasUnsavedChanges = !_.isEqual(data, initialDataRef.current);

    return (
        <>
            <input
                ref={hiddenInputRef}
                type="text"
                style={{
                    position: 'fixed',
                    opacity: 0,
                    pointerEvents: 'none',
                    width: 1,
                    height: 1,
                    top: 0,
                    left: 0,
                    fontSize: 16,
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                }}
                readOnly={false}
                tabIndex={-1}
                aria-hidden="true"
            />

            {/* Trigger button */}
            <button
                type="button"
                onClick={() => {
                    if (isMobile) {
                        hiddenInputRef.current?.focus();
                    }
                    captureSnapshot();
                    setPanelOpen(true);
                }}
                className="rfc-trigger-btn"
            >
                <div className="rfc-trigger-btn-left">
                    <i className="bx bx-car fs-5" />
                    {t('reimbursement.addMileageCompensation')}
                </div>
                <i className="bx bx-chevron-right fs-5" />
            </button>

            <SlidePanel
                isOpen={panelOpen}
                onClose={handleClose}
                title={t('reimbursement.addMileageCompensation')}
                zIndexOffset={20}
                variant="center"
                closeOnOutsideClick={false}
            >
                <div
                    ref={formDivRefMileage}
                    className="rfc-panel-body"
                    onBlur={(e) => {
                        if (!showErrors) e.preventDefault();
                    }}
                >

                    {/* Från / Till unified card */}
                    <div className="rfc-route-card">
                        {/* Från row */}
                        <div className="rfc-route-row-border">
                            <i className="bx bx-target-lock rfc-route-icon" />
                            <span className="rfc-route-label">
                                {t('reimbursement.origin') || "Från:"}
                            </span>
                            <div className="rfc-route-input-wrap position-relative">
                                <CustomInput
                                    model={RequestReimbursementMileage}
                                    attribute='from'
                                    data={data?.from}
                                    customChange={(e) => onChange(e, "from")}
                                    disableBlurValidation={true}
                                />
                                <div id="errorFrom" className="error tooltip-custom-out-label d-none show-custom rfc-error">
                                    {t('validationMessage.fieldRequired')}
                                </div>
                            </div>
                            {data?.from && (
                                <button
                                    type="button"
                                    onClick={() => onChange("", "from")}
                                    className="rfc-route-clear-btn"
                                >
                                    <i className="bx bx-x-circle" style={{ fontSize: 18 }} />
                                </button>
                            )}
                        </div>

                        {/* Swap icon */}
                        <div className="rfc-swap-wrap">
                            <button
                                type="button"
                                onClick={() => onChange(null, "swap")}
                                className="rfc-swap-btn"
                            >
                                <i className="bx bx-transfer" style={{ fontSize: 16, transform: "rotate(90deg)" }} />
                            </button>
                        </div>

                        {/* Till row */}
                        <div className="rfc-route-row">
                            <i className="bx bx-map rfc-route-icon" />
                            <span className="rfc-route-label">
                                {t('reimbursement.destination') || "Till:"}
                            </span>
                            <div className="rfc-route-input-wrap position-relative">
                                <CustomInput
                                    model={RequestReimbursementMileage}
                                    attribute='to'
                                    data={data?.to}
                                    customChange={(e) => onChange(e, "to")}
                                    disableBlurValidation={true}
                                />
                                <div id="errorTo" className="error tooltip-custom-out-label d-none show-custom rfc-error">
                                    {t('validationMessage.fieldRequired')}
                                </div>
                            </div>
                            {data?.to && (
                                <button
                                    type="button"
                                    onClick={() => onChange("", "to")}
                                    className="rfc-route-clear-btn"
                                >
                                    <i className="bx bx-x-circle" style={{ fontSize: 18 }} />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Total körsträcka */}
                    <div>
                        <div className="rfc-km-row">
                            <div className="rfc-km-input-wrap position-relative">
                                <CustomInput
                                    model={RequestReimbursementMileage}
                                    attribute='km'
                                    data={data?.km !== 0 ? data?.km : ""}
                                    customChange={(e) => onChange(e, "km")}
                                    disableBlurValidation={true}
                                />
                                <div id="errorKm" className="error tooltip-custom-normal d-none show-custom rfc-error">
                                    {t('validationMessage.greater0')}
                                </div>
                            </div>
                            <span className="rfc-km-equals">=</span>
                            <div className="rfc-km-amount">
                                {data?.amount !== 0 ? ToCustomDecimal(data?.amount || 0) : 0} kr
                            </div>
                        </div>
                    </div>

                    {/* Divider */}
                    <div className="rfc-dashed-divider" />

                    {/* Add passengers button */}
                    {(data?.passengers === null || data?.passengers?.length === 0) && (
                        <Col lg={12} className="d-flex justify-content-center">
                            <CustomButton
                                text={t('reimbursement.addPassengers')}
                                icon="bx bxs-plus-circle pe-3 text-green"
                                iconSize={4}
                                hoverIconSize={4}
                                customClasses='text-green bg-transparent border-0 rounded-2 fw-bold'
                                hoverButtonShadow={true}
                                onClick={() => onChange("", "addPassenger")}
                            />
                        </Col>
                    )}

                    {/* Passengers form */}
                    {data?.passengers !== null && data?.passengers?.length > 0 && (
                        <div className="rfc-passengers-wrap">
                            {/* Header */}
                            <div className="rfc-passengers-header">
                                <h5 className="rfc-passengers-title">{t('reimbursement.passengers')}</h5>
                                <button
                                    type="button"
                                    onClick={() => onChange("", "cancelPassenger")}
                                    className="rfc-cancel-passenger-btn"
                                >
                                    {t('reimbursement.cancelPassenger')}
                                </button>
                            </div>

                            {/* Info text */}
                            <div className="rfc-passengers-info">
                                <i className='bx bx-info-circle rfc-passengers-info-icon' />
                                <span>{t('reimbursement.passengersHelpText')}</span>
                            </div>

                            {/* Antal passagerare */}
                            <div className="position-relative">
                                <CustomInput
                                    model={RequestPassenger}
                                    attribute='quantity'
                                    data={data?.passengers[0]?.quantity !== 0 ? data?.passengers[0]?.quantity : ""}
                                    customChange={(e) => onChange(e, "quantityPassenger")}
                                    disableBlurValidation={true}
                                />
                                <div id="errorQuantityPassenger" className="error tooltip-custom-normal d-none show-custom rfc-error">
                                    {t('validationMessage.fieldRequired')}
                                </div>
                            </div>

                            {/* Sammanlagd sträcka */}
                            <div className="rfc-km-row">
                                <div className="rfc-km-input-wrap position-relative">
                                    <CustomInput
                                        model={RequestPassenger}
                                        attribute='km'
                                        data={data?.passengers[0]?.km !== 0 ? data?.passengers[0]?.km : ""}
                                        customChange={(e) => onChange(e, "kmPassenger")}
                                        disableBlurValidation={true}
                                    />
                                    <div id="errorKmPassenger" className="error tooltip-custom-normal d-none show-custom rfc-error">
                                        {t('validationMessage.greater0')}
                                    </div>
                                </div>
                                <span className="rfc-km-equals">=</span>
                                <div className="rfc-km-amount">
                                    {data?.passengers[0]?.amount !== 0 ? ToCustomDecimal(data?.passengers[0]?.amount) : 0} kr
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Cancel + Submit */}
                    <div className="rfc-submit-area">
                        {editMode && (
                            <Button
                                className="bg-secondary w-100"
                                onClick={() => { onCancel(cancelModel); setPanelOpen(false); }}
                            >
                                {t('button.cancel')}
                            </Button>
                        )}

                        {data?.passengers !== null && data?.passengers?.length > 0 ? (
                            <CustomSubmitButton
                                text={editMode === true ? t('button.save') : t('reimbursement.addMileage')}
                                customClick={async () => {
                                    setShowErrors(true);
                                    const isValid = await onValidate();
                                    if (isValid) setPanelOpen(false);
                                }}
                                formDivRef={formDivRefMileage}
                                customClass="w-100"
                                //disabled={isButtonDisabled}
                                //style={isButtonDisabled ? { opacity: 0.45, cursor: 'not-allowed' } : {}}
                            />
                        ) : (
                            <CustomSubmitButton
                                text={editMode === true ? t('button.save') : t('reimbursement.addCompensation')}
                                customClick={async () => {
                                    setShowErrors(true);
                                    const isValid = await onValidate();
                                    if (isValid) setPanelOpen(false);
                                }}
                                // formDivRef={formDivRefMileage}
                                customClass="w-100"
                                //disabled={isButtonDisabled}
                                //style={isButtonDisabled ? { opacity: 0.45, cursor: 'not-allowed' } : {}}
                            />
                        )}
                    </div>

                </div>
            </SlidePanel>
        </>
    );
}